import { useGridApiRef, Table } from '@repo/go-table';
import type { GridColumnResizeParams, GridEventListener } from '@repo/go-table';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { debounce } from 'throttle-debounce';

import type { INotificationsService } from '../../shared/INotificationsService';
import type { AlertsManagerWidgetState } from '../AlertsManagerWidgetState';
import tableStyles from '../AlertsManagerTable.module.css';
import { AlertRulesTableSettings } from '../components/AlertRulesTableSettings';
import type { ViewStore } from '../stores/ViewStore';
import { isColumnsOrderModelEqual } from '../utils';

import { AlertRulesTableStore } from './AlertRulesTableStore';
import {
  AlertRuleContextSelectionPanel,
  type AlertRuleContextDirectory,
} from './components/AlertRuleContextSelectionPanel';
import { AlertRulesActionsBar } from './components/AlertRulesActionsBar';
import { AlertRulesTableToolbar } from './components/AlertRulesTableToolbar';
import { TemplatesDrawer } from './components/TemplatesDrawer';
import type { AlertRule } from './services/AlertRule.entity';
import type { AlertRulesService } from './services/AlertRulesService';
import { useColumnGroups } from './useColumnGroups';
import { useContextOptions } from './useContextOptions';
import { useTableColumns } from './useTableColumns';

type AlertRulesTableProps = {
  viewStore: ViewStore;
  service: AlertRulesService;
  notifications: INotificationsService;
  widgetState: AlertsManagerWidgetState;
};

export const AlertRulesTable = observer(function AlertRulesTable({
  viewStore,
  service,
  notifications,
  widgetState,
}: AlertRulesTableProps) {
  const tableApi = useGridApiRef();
  const [wrapperElement, setWrapperElement] = useState<HTMLDivElement | null>(null);
  const [mainContentElement, setMainContentElement] = useState<HTMLDivElement | null>(null);

  const store = useMemo(
    () => new AlertRulesTableStore(tableApi, notifications, service),
    [service, notifications, tableApi],
  );

  const columns = useTableColumns(
    store,
    service,
    widgetState.timeZonesStore,
    widgetState.appParams.isAltNames,
    widgetState.defaultDepthUnit,
  );
  const groups = useColumnGroups(
    service.uiState.editing ? widgetState.defaultDepthUnit : service.settingsStore.scaleDepthUnit,
  );
  const columnFields = useMemo(() => columns.map((col) => col.field), [columns]);

  const { displayManager } = store;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleWidthChange = useCallback(
    debounce(300, ({ colDef: { field }, width }: GridColumnResizeParams) => {
      const columns = tableApi.current.getAllColumns();
      const updatedColumns: Record<string, number> = {};

      const columnsWidth = displayManager.getColumnsWidth();
      columns.forEach((column) => {
        if (column.field === field) {
          updatedColumns[field] = width;
        } else if (columnsWidth && !columnsWidth[column.field]) {
          updatedColumns[column.field] = column.computedWidth;
        }
      });

      displayManager.onWidthChange(updatedColumns);
    }),
    [displayManager],
  );

  const onCopy: GridEventListener<'clipboardCopy'> = useCallback(
    (_, event) => {
      event.defaultMuiPrevented = true;
      let timer: number | undefined;

      if (timer != null) {
        return;
      }

      store.handleCopy();

      timer = window.setTimeout(() => {
        timer = undefined;
      }, 300);
    },
    [store],
  );

  const onCellKeydown: GridEventListener<'cellKeyDown'> = useCallback(
    (params, event) => {
      if (event.code === 'KeyV' && (event.metaKey || event.ctrlKey)) {
        event.defaultMuiPrevented = true;
      }

      if (event.code === 'Delete') {
        event.defaultMuiPrevented = true;
        store.handleDelete(params);
      }
    },
    [store],
  );

  useEffect(() => {
    return tableApi.current.subscribeEvent('columnSeparatorDoubleClick', ({ field }) => {
      const col = tableApi.current.getColumn(field);
      const width = col.width;

      if (width != null) {
        handleWidthChange({ colDef: col, width });
      }
    });
  }, [displayManager, handleWidthChange, tableApi]);

  useEffect(() => {
    let timer: number | undefined;

    const handlePaste = (event: KeyboardEvent) => {
      if (event.code !== 'KeyV' || (!event.ctrlKey && !event.metaKey)) {
        return;
      }

      // игнорируем, если событие реагирует на вставку текста в input поле т.к. в таком случае должна отработать дефолтная логика вставки браузера
      if (event.target instanceof HTMLInputElement && ['text', 'number', 'date'].includes(event.target.type)) {
        return;
      }

      if (timer != null) {
        return;
      }

      event.preventDefault();

      store.handlePaste();

      timer = window.setTimeout(() => {
        timer = undefined;
      }, 300);
    };

    document.addEventListener('keydown', handlePaste);

    return () => {
      document.removeEventListener('keydown', handlePaste);
      window.clearTimeout(timer);
    };
  }, [store]);

  const columnsOrderModel = displayManager?.columnsOrderModel;

  const handleColumnsOrderModelChange = useCallback(
    (orderModel: string[]) => {
      const isOrderModelValid =
        orderModel.length === columnFields.length && orderModel.every((field) => columnFields.includes(field));

        
      if (!isOrderModelValid) {
        return;
      }
      
      const hasSavedOrderModel = Boolean(columnsOrderModel?.length);
      const hasCustomSavedOrderModel = hasSavedOrderModel && !isColumnsOrderModelEqual(columnsOrderModel, columnFields);
      const isIncomingDefaultOrderModel = isColumnsOrderModelEqual(orderModel, columnFields);


      if (hasCustomSavedOrderModel && isIncomingDefaultOrderModel) {
        return;
      }

      displayManager?.onColumnsOrderModelChange(orderModel);
    },
    [columnFields, columnsOrderModel, displayManager],
  );

  useEffect(() => {
    const columnsWidth = displayManager?.getColumnsWidth();
    if (!displayManager || !columnsWidth) {
      return;
    }

    for (const column of tableApi.current.getAllColumns()) {
      if (columnsWidth[column.field] === column.width) {
        continue;
      }

      tableApi.current.setColumnWidth(column.field, columnsWidth[column.field]);
    }
  }, [tableApi, displayManager]);

  useEffect(() => {
    return store.init();
  }, [store]);

  const getSelectedRowsIds = useCallback(() => {
    const selectedRows = tableApi.current.getSelectedRows();
    return Array.from(selectedRows.keys()).map((key) => key.toString());
  }, [tableApi]);

  const selectedRowIds = useMemo(() => {
    const model = store.selectedRows;
    if (!Array.isArray(model)) return [];
    return model.map((id) => String(id));
  }, [store.selectedRows]);

  const contextDirectories: AlertRuleContextDirectory[] = useContextOptions(service);

  const themeClassName = widgetState.appParams.theme === 'dark' ? 'go-widget-theme-dark' : 'go-widget-theme-light';
  return (
    <div className={clsx('go-widgets-widget-wrapper', themeClassName)} ref={(ref) => setWrapperElement(ref)}>
      <div
        className="go-widgets-content"
        style={{ height: '100%', position: 'relative' }}
        ref={(ref) => setMainContentElement(ref)}
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '0 16px 16px' }}>
          <AlertRulesTableToolbar
            viewStore={viewStore}
            onDelete={() => {
              service.deleteAlertRuleItems(getSelectedRowsIds());
            }}
            onCopy={() => store.handleCopy()}
            onPaste={() => store.handlePaste()}
            onAdd={() => service.addNewAlertRuleItems()}
            onChooseRow={() => store.setIsRowSelectionModeOn(!store.isRowSelectionModeOn)}
          />
          <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
            <Table<AlertRule>
              initialState={{
                sorting: {
                  sortModel: displayManager.sortingModel,
                },
                filter: {
                  filterModel: displayManager.filterModel,
                },
                columns: {
                  columnVisibilityModel: displayManager.columnsVisibilityModel,
                },
                pinnedColumns: displayManager.pinnedColumns,
              }}
              columnGroupingModel={groups}
              sortModel={displayManager.sortingModel}
              filterModel={displayManager.filterModel}
              checkboxSelection={store.isRowSelectingEnabled}
              columnVisibilityModel={displayManager.columnsVisibilityModel}
              columnsOrderModel={displayManager.columnsOrderModel}
              onColumnVisibilityModelChange={displayManager.onColumnVisibilityChange}
              onColumnsOrderModelChange={handleColumnsOrderModelChange}
              onPinnedColumnsChange={displayManager.onPinChange}
              onFilterModelChange={displayManager.onFilterChange}
              onSortModelChange={displayManager.onSortChange}
              onColumnResize={handleWidthChange}
              onRowSelectionModelChange={store.setSelectedRows}
              onClipboardCopy={onCopy}
              onCellKeyDown={onCellKeydown}
              isCellEditable={(params) => store.isCellEditable(params)}
              getRowId={(row) => row.entityId}
              apiRef={tableApi}
              className={tableStyles.table}
              columns={columns}
              editMode="cell"
              rows={service.rows}
              cellSelection
              loading={store.isLoading}
            />
          </div>
        </div>
        {store.isContextPanelOpen && mainContentElement && store.isEditing && (
          <AlertRuleContextSelectionPanel
            selectedRowIds={selectedRowIds}
            store={store}
            directories={contextDirectories}
            onClose={() => store.setIsContextPanelOpen(false)}
            container={mainContentElement}
          />
        )}
      </div>
      <div className="go-widgets-actions-bar-wrapper">
        <AlertRulesActionsBar widgetState={widgetState} store={store} service={service} />
      </div>
      <AlertRulesTableSettings
        displayManager={displayManager}
        columns={columns}
        groups={groups}
        open={store.isTableSettingsOpen}
        onClose={() => store.setIsTableSettingsOpen(false)}
        container={wrapperElement ?? undefined}
      />
      <TemplatesDrawer
        isOpen={service.isTemplatesDrawerOpen}
        onClose={() => service.setIsTemplatesDrawerOpen(false)}
        externalContainer={wrapperElement ?? undefined}
        service={service}
        store={store}
      />
    </div>
  );
});
